import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { Notification } from '@jisy/shared';
import { notificationsQuery, useMarkNotificationsRead } from '../api/queries.js';
import { formatRelative } from '../lib/format.js';
import { Avatar } from './Badges.js';
import styles from './NotificationsBell.module.css';

/**
 * Колокольчик уведомлений в шапке.
 *
 * Показывает только непрочитанное: это не лента событий, а напоминание о том, чего ещё не
 * видели. Поэтому нажатая строка исчезает из списка — задача открывается, и всё, что в ней
 * написали, считается просмотренным.
 *
 * Макета для колокольчика нет, как и для выбора языка рядом, — собран по токенам оформления.
 */
export function NotificationsBell({
  onOpenIssue,
}: {
  onOpenIssue: (issueKey: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const notifications = useQuery(notificationsQuery);
  const markRead = useMarkNotificationsRead();

  const items = notifications.data?.items ?? [];
  const unread = notifications.data?.unread ?? 0;

  function openIssue(issueKey: string): void {
    setOpen(false);
    onOpenIssue(issueKey);
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.trigger}
          aria-label={
            unread > 0 ? t('notifications.ariaUnread', { count: unread }) : t('notifications.title')
          }
        >
          <BellIcon />
          {unread > 0 && (
            <span className={styles.badge} aria-hidden="true">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={styles.content} align="end" sideOffset={6}>
          <div className={styles.heading}>
            <span className={styles.headingTitle}>{t('notifications.title')}</span>
            {items.length > 0 && (
              <button
                type="button"
                className={styles.markAll}
                onClick={() => markRead.mutate({ all: true })}
              >
                {t('notifications.markAll')}
              </button>
            )}
          </div>

          {notifications.isPending && <p className={styles.note}>{t('notifications.loading')}</p>}

          {notifications.isError && !notifications.isPending && (
            <p className={styles.note}>{t('notifications.failed')}</p>
          )}

          {!notifications.isPending && !notifications.isError && items.length === 0 && (
            <p className={styles.note}>{t('notifications.empty')}</p>
          )}

          {items.length > 0 && (
            <ul className={styles.list}>
              {items.map((item) => (
                <li key={item.id}>
                  <NotificationRow item={item} onOpen={() => openIssue(item.issueKey)} />
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function NotificationRow({
  item,
  onOpen,
}: {
  item: Notification;
  onOpen: () => void;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <button type="button" className={styles.item} onClick={onOpen}>
      <Avatar user={item.actor} size={26} />
      <span className={styles.body}>
        <span className={styles.line}>
          <span className={`${styles.actor} truncate`}>
            {item.actor?.displayName ?? t('notifications.someone')}
          </span>
          <span className={styles.when}>{formatRelative(item.createdAt, t)}</span>
        </span>

        <span className={styles.line}>
          <span className={`mono ${styles.issueKey}`}>{item.issueKey}</span>
          <span className={`${styles.summary} truncate`}>{item.issueSummary}</span>
        </span>

        {/* У назначения текста нет — вместо выдержки поясняем, что именно случилось. */}
        <span className={item.kind === 'assigned' ? styles.assigned : styles.excerpt}>
          {item.kind === 'assigned' ? t('notifications.assigned') : item.excerpt}
        </span>
      </span>
    </button>
  );
}

/** Колокольчик: та же линия в 1.2, что у значков боковой колонки. */
function BellIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d="M4.4 7.1a3.6 3.6 0 0 1 7.2 0c0 2.5.9 3.4.9 3.4H3.5s.9-.9.9-3.4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M6.7 12.5a1.4 1.4 0 0 0 2.6 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}
